<template>
  <div>
    <!-- INPUT PARA SELEÇÃO DE TODAS OPÇÕES -->
    <div v-if="toggleAll" class="checkbox-group--select-all">
      <InputCheckbox
        :label="toggleAllLabel"
        :checked="allChecked"
        @change="toggleAllEvent"
      />
    </div>

    <!-- GROUP -->
    <div v-if="hasGroups" class="checkbox-group">
      <div
        v-for="group in searchResult"
        :key="`${group.fieldset}-${new Date().getTime()}`"
        class="checkbox-group--item"
      >
        <label>{{ group.fieldset }}</label>

        <InputCheckbox
          v-for="(option, i) in group.subfields"
          :key="`${option.label}-${option.value}-${i}`"
          v-model="selectedValues"
          :label="option.label"
          :name="`g-checkbox-${i}-${group.fieldset}`"
          :inputValue="option"
          :checked="isChecked(option)"
          class="filter-dropdown-list--input"
          @change="handleEvents"
        />
      </div>
    </div>
    <!-- SINGLE -->
    <div v-else>
      <InputCheckbox
        v-for="(option, i) in searchResult"
        :key="`${option.label}-${option.value}-${i}`"
        v-model="selectedValues"
        :label="option.label"
        :name="`checkbox-${i}`"
        :inputValue="option"
        :checked="isChecked(option)"
        class="filter-dropdown-list--input"
        @change="handleEvents"
      />
    </div>
  </div>
</template>

<script>
/**
 * Componente que compõe o FilterDropdown
 */
import isEqual from 'lodash.isequal'
import { getRandomInt } from '@/helpers/math'
import InputCheckbox from '@/components/_base/elements/InputCheckbox'

export default {
  name: 'FilterDropdownCheckbox',
  components: {
    InputCheckbox
  },
  props: {
    value: [Array, Number, String, Object],
    /**
     * Array de objetos das opções
     * Obj exemplo:
     * {
     *  label: 'Nome da opção',
     *  value: 0,
     *  checked: false
     * }
     */
    options: {
      type: Array,
      default: () => []
    },
    /**
     * Termo pesquisado
     */
    searchTerm: {
      type: String,
      default: ''
    },
    /**
     * Campos do array de opções que devem ser procurados
     * @default {String} label
     */
    fieldsToSearch: {
      type: [Array], // array de strings com os nomes das propriesdades a serem pesquisadas
      default: () => ['label']
    },
    /**
     * Se deve apresentar uma opção para selecionar todas as opções
     */
    toggleAll: {
      type: Boolean,
      default: true
    },
    /**
     * Texto da opção de seleção de todas
     */
    toggleAllLabel: {
      type: String,
      default: 'Todas'
    }
  },
  data() {
    return {
      name: `radio-${getRandomInt(0, 1000)}`,
      selectedValues: []
    }
  },
  computed: {
    hasGroups() {
      return this.options[0]?.fieldset ? true : false
    },
    /** Retorna apenas os grupos que possuem opções */
    ratesWithin() {
      return this.options.filter((item) => item.subfields.length > 0)
    },
    /**
     * Retorna se todas as opções estão selecionadas
     * @returns {Boolean}
     */
    allChecked() {
      const allOptions = this.searchResult.flatMap(
        (item) => item.subfields || item
      )

      return (
        /**
         * Verifica se selectedValues.length é maior ou igual a allOptions.length
         * porque pode haver a situação em que o usuário seleciona todas opções e após
         * usa o filtro, fazendo que o número de opções possa ficar menor que o de seleção
         */
        allOptions.length > 0 && this.selectedValues.length >= allOptions.length
      )
    },
    /**
     * Propriedade que filtra as opções de acordo com o termo buscado
     * @returns {Array}
     */
    searchResult() {
      if (this.searchTerm.trim() === '')
        return this.hasGroups ? this.ratesWithin : this.options

      const rgx = new RegExp(this.searchTerm, 'gmi')

      const result = []
      const optionsList = this.hasGroups ? this.ratesWithin : this.options

      /** Itera pelas opções que possuem tarifas */
      optionsList.forEach((item) => {
        if (item.label?.search(rgx) > -1) {
          result.push(item)
        }

        // se é um grupo filtra os filhos
        if (item.fieldset) {
          const itemCopy = JSON.parse(JSON.stringify(item))
          const group = [...item.subfields]
          itemCopy.subfields = group.filter(
            (item) => item.label.search(rgx) > -1
          )

          if (itemCopy.subfields.length > 0) result.push(itemCopy)
        }
      })

      return result
    }
  },
  watch: {
    value(newValue) {
      this.selectedValues = newValue
    }
  },
  created() {
    if (this.options[0]?.fieldset) {
      this.selectedValues = [...this.selectedValues, ...this.value]
    } else {
      this.selectedValues = this.value
    }

    this.$emit('input', this.selectedValues)
  },
  methods: {
    /** Marca ou desmarca todas as opções apresentadas */
    toggleAllEvent(checked) {
      if (checked) {
        if (this.searchResult[0].subfields) {
          this.selectedValues = [
            ...this.searchResult.flatMap((item) => item.subfields)
          ]
        } else {
          this.selectedValues = [...this.searchResult]
        }
      } else {
        this.selectedValues = []
      }
      this.$emit('input', this.selectedValues)
    },
    /** Emite um evento ao marcar ou desmarcar um input */
    handleEvents() {
      this.$emit('input', this.selectedValues)
    },
    /** Verifica se a opção está marcada */
    isChecked(option) {
      return this.value?.findIndex((item) => isEqual(item, option)) > -1
    }
  }
}
</script>

<style lang="scss" scoped>
.filter-dropdown-list {
  &--input + &--input {
    margin-top: 18px;
  }
}

.checkbox-group {
  &--select-all {
    padding-bottom: 12px;
    margin-bottom: 14px;
    border-bottom: 1px solid #e8e8e8;
  }

  label {
    margin-bottom: 10px;
    font-size: font-size('size-medium');
    font-weight: font-weight('weight-medium');
    white-space: nowrap;
  }

  &--item + &--item {
    margin-top: 18px;
    padding-top: 10px;
    border-top: 1px solid $gray-border;
  }
}
</style>
